import { useEffect, useState, useCallback } from "react";
import * as Sentry from '@sentry/react';
import { loadGoogleMaps, isGoogleMapsLoaded } from './googleMaps';



export type Suggestion = {
  label: string;
  description: string;
  value: string;
  placeId: string;
  tags: string[];
};
const SKIP_TAGS = ['establishment', 'point_of_interest', 'tourist_attraction'];

export function formatTag(tag: string): string {
  const words = tag.toLowerCase().replaceAll('_', ' ').split(' ');
  return words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

export function filterHierarchicalTags(tags: string[]): string[] {
  // Filter out unwanted tags first.
  const filtered = tags.filter((tag) => !SKIP_TAGS.includes(tag));
  // Format all tags to a consistent style.
  const formatted = filtered.map(formatTag);

  // Sort tags by descending number of words (more specific tags first).
  formatted.sort((a, b) => b.split(' ').length - a.split(' ').length);

  const finalTags: string[] = [];
  for (const tag of formatted) {
    // Check if the current tag is contained as a whole word in any of the finalTags.
    // Here we simply check if the lowercased tag exists in the lowercased final tag.
    // For more robust matching, you could use regex with word boundaries.
    const isSubset = finalTags.some((existing) =>
      new RegExp(`\\b${tag.toLowerCase()}\\b`).test(existing.toLowerCase()),
    );
    if (!isSubset) {
      finalTags.push(tag);
    }
  }
  return finalTags;
}



export type UseMapsSuggestionsProps = {
    selectedItems: Suggestion[];
    setSelectedItems: (items: Suggestion[]) => void;
}


export function useGoogleMapsSuggestions(props: UseMapsSuggestionsProps) {
    const { selectedItems, setSelectedItems } = props;
    const [name, setName] = useState<string>('');
    const [options, setOptions] = useState<Suggestion[]>([]);
    // Status can be 'pending', 'loading', 'finished', or 'error'
    const [status, setStatus] = useState<'pending' | 'loading' | 'finished' | 'error' | undefined>('pending');
    const [sessionToken, setSessionToken] = useState<
      google.maps.places.AutocompleteSessionToken | undefined
    >(undefined);
  
    useEffect(() => {
      const initializeGoogleMaps = async () => {
        try {
          if (!isGoogleMapsLoaded()) {
            await loadGoogleMaps();
          }
          if (window.google?.maps?.places) {
            const sessionToken = new window.google.maps.places.AutocompleteSessionToken();
            setSessionToken(sessionToken);
          }
        } catch (error) {
          console.error('Failed to load Google Maps:', error);
          Sentry.captureException(error);
        }
      };
  
      initializeGoogleMaps();
    }, []);
  
    // Function to load suggestions using Google Places Autocomplete.
    const loadSuggestions = useCallback(
      async (value: string) => {
        const inputValue = value.trim();
        if (!inputValue) {
          setOptions([]);
          setStatus('pending');
          return;
        }
  
        // Ensure the Google API is loaded.
        if (!isGoogleMapsLoaded() || !window.google?.maps?.places) {
          try {
            await loadGoogleMaps();
          } catch (error) {
            console.error('Google Maps JavaScript API is not loaded');
            Sentry.captureException('Google Maps JavaScript API is not loaded');
            setStatus('error');
            return;
          }
        }
  
        let localSessionToken = sessionToken;
        if (!localSessionToken) {
          localSessionToken = new window.google.maps.places.AutocompleteSessionToken();
          setSessionToken(localSessionToken);
        }
  
        setStatus('loading');
        try {
          const predictions =
            await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
              input: inputValue,
              sessionToken: localSessionToken,
              // Restrict to businesses (establishments)
              includedPrimaryTypes: ['establishment'],
            });
  
          // Map the predictions to an array of option objects.
          const suggestions =
            predictions.suggestions
              .filter((prediction) => prediction.placePrediction)
              .map(
                (prediction) =>
                  ({
                    value: prediction.placePrediction?.placeId ?? '',
                    placeId: prediction.placePrediction?.placeId ?? '',
                    label: prediction.placePrediction?.mainText?.text ?? '',
                    description: prediction.placePrediction?.secondaryText?.text ?? '',
                    tags: filterHierarchicalTags(prediction.placePrediction?.types ?? []),
                  }) as Suggestion,
              ) ?? [];
          // Filter out suggestions that are already selected.
          const filteredSuggestions = suggestions.filter(
            (suggestion) => !selectedItems.some((item) => item.placeId === suggestion.placeId),
          );
          setOptions(filteredSuggestions);
          // You might set 'finished' if you expect no further pagination.
          setStatus('finished');
        } catch (error) {
          console.error('Error loading suggestions', error);
  
          Sentry.captureException(error, {
            tags: {
              inputValue,
            },
          });
          setOptions([]);
          setStatus('error');
          return;
        }
      },
      [setOptions, setStatus, sessionToken],
    );

    return {
        name,
        setName,
        options,
        setOptions,
        status,
        loadSuggestions,
    };
}